import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

/* El SDK de PayPal (script externo de paypal.com) puede emitir errores cross-origin
   genéricos ("Script error.") que el overlay de desarrollo de CRA muestra en rojo
   aunque son inofensivos y no aparecen en producción. Los silenciamos (fase de captura). */
if (typeof window !== "undefined" && !window.__etErrSuppressed) {
  window.__etErrSuppressed = true;
  const benign = (m, f) => (typeof m === "string" && m.includes("Script error")) || (typeof f === "string" && f.includes("paypal"));
  window.addEventListener("error", (e) => {
    if (benign(e.message, e.filename)) { e.stopImmediatePropagation(); e.preventDefault(); }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const r = String((e && e.reason) || "");
    if (r.includes("paypal") || r.includes("Script error")) { e.stopImmediatePropagation(); e.preventDefault(); }
  }, true);
}
import {
  MapPin, CalendarDays, Moon, Users, Download, Clock, Compass, BedDouble,
  Plane, Utensils, CheckCircle2, XCircle, Sparkles, ArrowRight, Phone, Mail,
  MessageCircle, Info, ShieldCheck, Globe2,
} from "lucide-react";

/* Espíritu Travel · Trip Plan mockup — un componente, dos variantes:
   variant="proposal"  → venta: imágenes en todo, 1 precio final/persona, incluye/no incluye, condiciones, aprobar
   variant="documents" → post-compra: SIN precios, fotos pequeñas/casi nulas, texto y legibilidad, contacto + info país */
const C = { navy: "#121b28", sand: "#f4ebd7", terra: "#e37e5e", olive: "#8a9771", magenta: "#c94277" };
const KANIT = { fontFamily: "'Kanit', system-ui, sans-serif" };
const RALE = { fontFamily: "'Raleway', system-ui, sans-serif" };
const ET_LOGO_SAND = "/brand/et-logo-sand.png";
const ET_LOGO_NAVY = "/brand/et-logo-navy.png";

const IMG = {
  alhambra1: "https://images.pexels.com/photos/33524278/pexels-photo-33524278.jpeg?auto=compress&cs=tinysrgb&w=1600",
  alhambra2: "https://images.unsplash.com/photo-1564697546537-e71eb2aaad1b?w=1200&q=80",
  alhambra3: "https://images.unsplash.com/photo-1620677368158-32b1293fac36?w=1200&q=80",
  alhambra4: "https://images.unsplash.com/photo-1605654464243-3668a4c0de3d?w=1200&q=80",
  marrakech1: "https://images.unsplash.com/photo-1587974928442-77dc3e0dba72?w=1600&q=80",
  souk1: "https://images.unsplash.com/photo-1517821099606-cef63a9bcda6?w=1200&q=80",
  souk2: "https://images.unsplash.com/photo-1569440703456-29b9c31765ca?w=1200&q=80",
  lamps: "https://images.unsplash.com/photo-1605882090044-aa9cbe6df29e?w=1200&q=80",
  riad1: "https://images.unsplash.com/photo-1762537628859-64af506dcedf?w=1200&q=80",
  riad2: "https://images.unsplash.com/photo-1758621664490-d144666a1488?w=1200&q=80",
  riad3: "https://images.pexels.com/photos/34332420/pexels-photo-34332420.jpeg?auto=compress&cs=tinysrgb&w=1200",
  riad4: "https://images.pexels.com/photos/31356131/pexels-photo-31356131.png?auto=compress&cs=tinysrgb&w=1200",
  tagine: "https://images.pexels.com/photos/22890025/pexels-photo-22890025.jpeg?auto=compress&cs=tinysrgb&w=1200",
  tagine2: "https://images.pexels.com/photos/30068444/pexels-photo-30068444.jpeg?auto=compress&cs=tinysrgb&w=1200",
  tapas: "https://images.unsplash.com/photo-1780821531390-d381eb3fc3e9?w=1200&q=80",
};

const TRIP = {
  title: "Spain & Morocco — A Journey Between Two Shores",
  client: "Ann Marie Bland",
  destination: "Spain · Morocco",
  start: "19 Jun", end: "28 Jun 2026", days: 10, nights: 9, travellers: 2,
  hero: [IMG.alhambra1, IMG.marrakech1, IMG.riad2],
  intro: "Ten unhurried days drifting from the Moorish palaces of Andalucía to the spice-scented medinas of Morocco. Stone, light, mint tea and the call to prayer at dusk — this is a journey designed to be felt, not just seen.",
  specialist: { name: "Marta Bermejo", role: "Your travel specialist", photo: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&q=80" },
  pricePerPerson: "€4,950",
  includes: [
    "9 nights in hand-picked boutique hotels & riads with daily breakfast",
    "First-class AVE train Madrid → Granada",
    "All private transfers with English-speaking drivers",
    "Private guided Alhambra & Generalife tour (skip-the-line)",
    "Atlas Mountains & Berber village private day",
    "24/7 in-destination support from your Espíritu Travel team",
  ],
  excludes: [
    "International flights to Madrid / from Marrakech",
    "Lunches and dinners not specified",
    "Travel insurance",
    "Personal expenses, tips and optional activities",
  ],
  booking: "A 30% deposit confirms your trip. The remaining balance is due 45 days before departure. Prices are per person based on two sharing.",
  cancellation: "Free changes up to 60 days before departure. 60–45 days: deposit retained. Under 45 days: cancellation fees apply per our terms, which we'll share in full with your confirmation.",
  contact: {
    phone: "+34 911 23 45 67",
    whatsapp: "+34 600 123 456",
    email: "hola@espiritutravel.com",
    emergency: "+34 600 999 000 (24/7 in-destination line)",
  },
  country: [
    { Icon: Globe2, t: "Languages", d: "Spanish in Spain; Arabic & French in Morocco. English is widely understood in hotels and tourist areas." },
    { Icon: Info, t: "Currency", d: "Euro (€) in Spain; Moroccan Dirham (MAD) in Morocco — withdraw small amounts from ATMs; cards accepted in most hotels and restaurants." },
    { Icon: ShieldCheck, t: "Good to know", d: "Type C/F plugs (Spain) and C/E (Morocco). Dress modestly in medinas and religious sites. Tap water: drink bottled in Morocco." },
  ],
};

const DAYS = [
  { id: "jun-19", tab: "Jun 19", date: "Thu, 19 Jun", city: "Madrid", blocks: [
    { kind: "flight", airline: "Iberia", flightNo: "IB 3171", from: "London Heathrow", fromCode: "LHR", to: "Madrid Barajas", toCode: "MAD", dep: "09:40", arr: "13:05", dur: "2h 25m" },
    { kind: "transfer", status: "Confirmed", title: "Private transfer · Airport to Hotel", img: IMG.alhambra4, meeting: "MAD Arrivals, Terminal 4", time: "13:30", dur: "35 min", desc: "Your English-speaking driver will meet you in arrivals holding a sign with your name, and whisk you into the heart of Madrid." },
    { kind: "note", status: "Recommended", title: "Evening at leisure in Madrid", desc: "Settle in and let the city come to you. A stroll through Barrio de las Letras, then a glass of Rioja on a quiet plaza — the perfect soft landing before the journey south." },
  ]},
  { id: "jun-20", tab: "Jun 20", date: "Fri, 20 Jun", city: "Granada", blocks: [
    { kind: "transfer", status: "Confirmed", title: "AVE high-speed train · Madrid → Granada", img: IMG.alhambra3, meeting: "Madrid Atocha", time: "08:25", dur: "3h 10m", desc: "Glide south through olive country in first class. Coffee, a window seat, and the Sierra Nevada rising on the horizon as you arrive in Granada." },
    { kind: "stay", name: "Palacio de Santa Paula, Granada", city: "Granada", address: "Gran Vía de Colón 31, 18001 Granada", checkin: "Fri, 20 Jun", checkout: "Mon, 23 Jun", nights: 3, room: "Junior Suite · Cloister view", travellers: "1 room · 2 adults", conf: "GR-44510-001654",
      gallery: [IMG.alhambra4, IMG.riad3, IMG.riad4, IMG.alhambra2],
      desc: "A restored 16th-century convent wrapped around a Moorish courtyard, moments from the cathedral. Vaulted ceilings, a candle-lit cloister and a rooftop with the Alhambra glowing across the valley.",
      perks: ["Upgrade on arrival, subject to availability", "Daily breakfast for two", "€100 hotel credit per stay"] },
  ]},
  { id: "jun-21", tab: "Jun 21", date: "Sat, 21 Jun", city: "Granada", blocks: [
    { kind: "activity", status: "Confirmed", title: "Private Alhambra & Generalife tour", img: IMG.alhambra1, meeting: "Hotel lobby", time: "08:30", dur: "3.5 hours",
      desc: "Beat the crowds with first-entry access to the Nasrid Palaces. Your art-historian guide unpicks the poetry carved into the walls — the geometry, the water, the light — in what may be the most beautiful building you will ever stand inside." },
    { kind: "restaurant", title: "Dinner at Carmen de Aben Humeya", img: IMG.tapas, time: "20:30",
      desc: "A carmen — a traditional Granadan garden house — clinging to the Albaicín hillside, with the floodlit Alhambra filling the view. Andalusian plates, a long wine list and a sunset you'll talk about for years." },
  ]},
  { id: "jun-22", tab: "Jun 22", date: "Sun, 22 Jun", city: "Albaicín", blocks: [
    { kind: "note", status: "Recommended", title: "Albaicín & Sacromonte at your own pace", desc: "Lose yourself in the white-washed lanes of the old Moorish quarter. We'll drop a short list of our favourite miradores and tea houses nearer the time — and where to hear flamenco the way the locals do." },
  ]},
  { id: "jun-23", tab: "Jun 23", date: "Mon, 23 Jun", city: "Marrakech", blocks: [
    { kind: "flight", airline: "Royal Air Maroc", flightNo: "AT 971", from: "Málaga", fromCode: "AGP", to: "Marrakech Menara", toCode: "RAK", dep: "15:20", arr: "16:10", dur: "1h 50m" },
    { kind: "transfer", status: "Confirmed", title: "Private transfer · Airport to Riad", img: IMG.souk1, meeting: "RAK Arrivals", time: "16:40", dur: "30 min", desc: "Met on arrival and driven to the edge of the medina, where a porter will guide you on foot through the lanes to your riad door." },
    { kind: "stay", name: "Riad Dar Anika, Marrakech", city: "Medina, Marrakech", address: "Derb Jdid 12, Medina, 40000 Marrakech", checkin: "Mon, 23 Jun", checkout: "Sat, 28 Jun", nights: 5, room: "Garden Suite · Plunge pool", travellers: "1 room · 2 adults", conf: "RAK-5112-094647",
      gallery: [IMG.riad2, IMG.riad1, IMG.riad3, IMG.lamps],
      desc: "A jewel-box riad hidden behind an unmarked door: a tiled courtyard, orange trees, a cool plunge pool and a rooftop for breakfast under the swifts. Staff who learn your name within the hour.",
      perks: ["Complimentary room upgrade at check-in, subject to availability", "Welcome mint tea & pastries on arrival", "One complimentary hammam ritual per guest"] },
  ]},
  { id: "jun-24", tab: "Jun 24", date: "Tue, 24 Jun", city: "Marrakech", blocks: [
    { kind: "activity", status: "Recommended", title: "Souks & hidden medina walk", img: IMG.souk2, meeting: "Riad door", time: "10:00", dur: "3 hours",
      desc: "A local guide leads you off the tourist trail — through the dyers' souk, the metalworkers' alley and a rooftop for mint tea above the chaos. Haggling lessons included." },
    { kind: "restaurant", title: "Dinner at Le Jardin", img: IMG.tagine, time: "20:00",
      desc: "A leafy courtyard restaurant tucked in the medina, all banana plants and brass lanterns. Modern Moroccan cooking — think slow-cooked lamb tagine with prunes and almonds — in one of the city's prettiest rooms." },
  ]},
  { id: "jun-25", tab: "Jun 25", date: "Wed, 25 Jun", city: "Atlas Mountains", blocks: [
    { kind: "activity", status: "Confirmed", title: "Atlas Mountains & Berber village day", img: IMG.alhambra3, meeting: "Riad lobby", time: "09:00", dur: "Full day",
      desc: "Trade the city for the High Atlas. A scenic drive to the foothills, a gentle guided walk between Berber villages, lunch with a local family and tea looking back at the snow-capped peaks." },
  ]},
  { id: "jun-26", tab: "Jun 26", date: "Thu, 26 Jun", city: "Marrakech", blocks: [
    { kind: "note", status: "Recommended", title: "Hammam, gardens & a slow day", desc: "You've earned a slow one. The Jardin Majorelle in the morning before it fills, then an afternoon hammam and a long lunch by the pool. We can book the spa — just say the word." },
    { kind: "restaurant", title: "Sunset dinner at Nomad", img: IMG.tagine2, time: "19:30",
      desc: "A rooftop overlooking the spice square, perfect for golden hour. Contemporary Moroccan small plates and a cold glass of something as the medina lights flicker on below." },
  ]},
  { id: "jun-27", tab: "Jun 27", date: "Fri, 27 Jun", city: "Marrakech", blocks: [
    { kind: "note", status: "Recommended", title: "Last day · your way", desc: "Keep this one flexible — a final wander, a few gifts from the souk, or simply the rooftop and a book. Nearer the time we'll leave a short list of last-minute favourites." },
  ]},
  { id: "jun-28", tab: "Jun 28", date: "Sat, 28 Jun", city: "Departure", blocks: [
    { kind: "transfer", status: "Confirmed", title: "Private transfer · Riad to Airport", meeting: "Riad door", time: "09:30", dur: "30 min", desc: "A porter walks your bags out of the medina to a waiting car for your transfer to Menara airport." },
    { kind: "flight", airline: "British Airways", flightNo: "BA 2667", from: "Marrakech Menara", fromCode: "RAK", to: "London Gatwick", toCode: "LGW", dep: "12:55", arr: "16:40", dur: "3h 45m" },
  ]},
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

const Detail = ({ Icon, children }) => (
  <span className="flex items-center gap-1.5 text-sm" style={{ color: `${C.navy}b3` }}>
    <Icon className="h-3.5 w-3.5" style={{ color: C.terra }} /> {children}
  </span>
);

const StatusLabel = ({ status }) => (
  <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: status === "Confirmed" ? C.olive : C.terra }}>{status}</span>
);

/* compact = Travel Documents (texto, sin/poca imagen). rich = Travel Proposal (imagen en todo). */
const ActivityBlock = ({ b, compact, testid }) => (
  <div className={compact ? "" : "grid gap-5 sm:grid-cols-5"} data-testid={testid}>
    {!compact && b.img && (
      <div className="sm:col-span-2 overflow-hidden rounded-lg">
        <img src={b.img} alt={b.title} loading="lazy" className="h-48 w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-full" />
      </div>
    )}
    <div className={!compact && b.img ? "sm:col-span-3" : ""}>
      <StatusLabel status={b.status} />
      <h4 className={`mt-1 ${compact ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"}`} style={KANIT}>{b.title}</h4>
      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
        {b.meeting && <Detail Icon={MapPin}>{b.meeting}</Detail>}
        {b.time && <Detail Icon={Clock}>{b.time}</Detail>}
        {b.dur && <Detail Icon={Compass}>{b.dur}</Detail>}
      </div>
      <p className="mt-3 leading-relaxed" style={{ color: `${C.navy}cc`, fontSize: compact ? "1.0625rem" : undefined }}>{b.desc}</p>
    </div>
  </div>
);

const RestaurantBlock = ({ b, compact, testid }) => (
  <div className={compact ? "" : "grid gap-5 sm:grid-cols-5"} data-testid={testid}>
    {!compact && b.img && (
      <div className="sm:col-span-2 overflow-hidden rounded-lg">
        <img src={b.img} alt={b.title} loading="lazy" className="h-44 w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-full" />
      </div>
    )}
    <div className={!compact && b.img ? "sm:col-span-3" : ""}>
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: C.magenta }}>
        <Utensils className="h-3.5 w-3.5" /> Where to eat
      </span>
      <h4 className={`mt-1 ${compact ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"}`} style={KANIT}>{b.title}</h4>
      {b.time && <div className="mt-1"><Detail Icon={Clock}>Reservation · {b.time}</Detail></div>}
      <p className="mt-2 leading-relaxed" style={{ color: `${C.navy}cc`, fontSize: compact ? "1.0625rem" : undefined }}>{b.desc}</p>
    </div>
  </div>
);

const NoteBlock = ({ b, compact, testid }) => (
  <div data-testid={testid}>
    <StatusLabel status={b.status} />
    <h4 className={`mt-1 ${compact ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"}`} style={KANIT}>{b.title}</h4>
    <p className="mt-2 leading-relaxed" style={{ color: `${C.navy}cc`, fontSize: compact ? "1.0625rem" : undefined }}>{b.desc}</p>
  </div>
);

const FlightCard = ({ b, testid }) => (
  <div className="overflow-hidden rounded-xl border" style={{ borderColor: `${C.navy}1a`, background: "#fff" }} data-testid={testid}>
    <div className="flex items-center justify-between px-5 py-2.5" style={{ background: C.navy, color: C.sand }}>
      <span className="flex items-center gap-2 text-sm font-semibold"><Plane className="h-4 w-4" style={{ color: C.terra }} /> {b.airline}</span>
      <span className="text-xs tracking-widest opacity-80">{b.flightNo}</span>
    </div>
    <div className="flex items-center justify-between px-6 py-5">
      <div className="text-center">
        <div className="text-2xl font-bold" style={KANIT}>{b.fromCode}</div>
        <div className="text-[11px] uppercase tracking-wider" style={{ color: `${C.navy}99` }}>{b.from}</div>
        <div className="mt-1 text-sm font-semibold" style={{ color: C.navy }}>{b.dep}</div>
      </div>
      <div className="flex flex-1 flex-col items-center px-3">
        <span className="text-[11px]" style={{ color: `${C.navy}80` }}>{b.dur}</span>
        <div className="my-1 flex w-full items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: C.terra }} />
          <span className="h-px flex-1" style={{ background: `${C.navy}33` }} />
          <Plane className="h-3.5 w-3.5" style={{ color: C.terra }} />
          <span className="h-px flex-1" style={{ background: `${C.navy}33` }} />
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: C.terra }} />
        </div>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: `${C.navy}80` }}>Direct</span>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold" style={KANIT}>{b.toCode}</div>
        <div className="text-[11px] uppercase tracking-wider" style={{ color: `${C.navy}99` }}>{b.to}</div>
        <div className="mt-1 text-sm font-semibold" style={{ color: C.navy }}>{b.arr}</div>
      </div>
    </div>
  </div>
);

const StayBlock = ({ b, compact, testid }) => {
  if (compact) {
    return (
      <div className="rounded-xl border p-5" style={{ borderColor: `${C.navy}1a`, background: "#fff" }} data-testid={testid}>
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: C.olive }}>
          <BedDouble className="h-3.5 w-3.5" /> Where you stay
        </span>
        <h4 className="mt-1 text-xl sm:text-2xl" style={KANIT}>{b.name}</h4>
        <p className="mt-2 leading-relaxed" style={{ color: `${C.navy}cc`, fontSize: "1.0625rem" }}>{b.desc}</p>
        <div className="mt-3 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
          <Detail Icon={CalendarDays}>{b.checkin} – {b.checkout} · {b.nights} nights</Detail>
          <Detail Icon={BedDouble}>{b.room} · {b.travellers}</Detail>
          <a href={`https://maps.google.com/?q=${encodeURIComponent(b.address)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm underline" style={{ color: C.navy }}>
            <MapPin className="h-3.5 w-3.5" style={{ color: C.terra }} /> {b.address}
          </a>
          <Detail Icon={ShieldCheck}>Confirmation: {b.conf}</Detail>
        </div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-2xl border" style={{ borderColor: `${C.navy}1a`, background: "#fff" }} data-testid={testid}>
      <div className="grid grid-cols-4 gap-1.5 p-1.5">
        <div className="col-span-2 row-span-2 overflow-hidden rounded-l-xl">
          <img src={b.gallery[0]} alt={b.name} loading="lazy" className="h-full w-full object-cover" style={{ minHeight: 240 }} />
        </div>
        {b.gallery.slice(1, 4).map((g, i) => (
          <div key={i} className={`overflow-hidden ${i === 0 ? "col-span-2 rounded-tr-xl" : ""} ${i === 2 ? "rounded-br-xl" : ""}`}>
            <img src={g} alt={b.name} loading="lazy" className="h-full w-full object-cover" style={{ minHeight: 117 }} />
          </div>
        ))}
      </div>
      <div className="p-6">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: C.olive }}>
          <BedDouble className="h-3.5 w-3.5" /> Where you stay
        </span>
        <h4 className="mt-1 text-2xl sm:text-3xl" style={KANIT}>{b.name}</h4>
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
          <Detail Icon={MapPin}>{b.city}</Detail>
          <Detail Icon={CalendarDays}>{b.checkin} – {b.checkout}</Detail>
          <Detail Icon={Moon}>{b.nights} nights</Detail>
        </div>
        <p className="mt-3 leading-relaxed" style={{ color: `${C.navy}cc` }}>{b.desc}</p>
        <div className="mt-5 rounded-lg border p-4" style={{ borderColor: `${C.olive}40`, background: `${C.olive}10` }}>
          <p className="text-sm font-semibold" style={{ color: C.navy }}>As clients of Espíritu Travel, you'll enjoy:</p>
          <ul className="mt-2 space-y-1.5">
            {b.perks.map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: `${C.navy}cc` }}>
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: C.olive }} /> {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const renderBlock = (b, di, bi, compact) => {
  const t = `mock-day-${di}-block-${bi}`;
  if (b.kind === "stay") return <StayBlock b={b} compact={compact} testid={t} />;
  if (b.kind === "flight") return <FlightCard b={b} testid={t} />;
  if (b.kind === "restaurant") return <RestaurantBlock b={b} compact={compact} testid={t} />;
  if (b.kind === "note") return <NoteBlock b={b} compact={compact} testid={t} />;
  return <ActivityBlock b={b} compact={compact} testid={t} />;
};

const IncExcCol = ({ title, items, Icon, color }) => (
  <div>
    <h3 className="mb-3 flex items-center gap-2 text-xl" style={KANIT}><Icon className="h-5 w-5" style={{ color }} /> {title}</h3>
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-2" style={{ color: `${C.navy}cc` }}>
          <Icon className="mt-1 h-4 w-4 shrink-0" style={{ color }} /> {it}
        </li>
      ))}
    </ul>
  </div>
);

export default function TripPlanMockup({ variant = "proposal" }) {
  const isProposal = variant === "proposal";
  const compact = !isProposal;
  const [heroIdx, setHeroIdx] = useState(0);
  const [active, setActive] = useState(DAYS[0].id);
  const [approved, setApproved] = useState(false);
  const [ppClientId, setPpClientId] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [daysUntil, setDaysUntil] = useState(120);
  const [paid, setPaid] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const sectionRefs = useRef({});
  const approveRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;
  const TOTAL = 9900;
  const payInFull = daysUntil < 60;
  const dueNow = payInFull ? TOTAL : Math.round(TOTAL * 0.3);
  const goToPay = () => {
    const el = approveRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // Re-alinear: las imágenes lazy y las animaciones desplazan el destino al hacer scroll.
    setTimeout(() => approveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 700);
    setTimeout(() => approveRef.current?.scrollIntoView({ behavior: "auto", block: "start" }), 1500);
  };

  useEffect(() => {
    if (!isProposal) return;
    fetch(`${API}/api/paypal/config`).then((r) => r.json()).then((d) => setPpClientId(d.client_id)).catch(() => {});
  }, [isProposal, API]);

  useEffect(() => {
    if (!isProposal) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % TRIP.hero.length), 5000);
    return () => clearInterval(t);
  }, [isProposal]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const goTo = (id) => { const el = sectionRefs.current[id]; if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div style={{ ...RALE, backgroundColor: C.sand, color: C.navy }} className="min-h-screen" data-testid={`trip-${variant}`}>
      {/* Doc-type badge */}
      <div className="fixed left-4 top-4 z-50 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] print:hidden"
        style={{ background: isProposal ? C.terra : C.navy, color: "#fff" }} data-testid="mock-doctype">
        {isProposal ? "Travel Proposal" : "Travel Documents"}
      </div>

      {/* HERO — rich for proposal, slim/text-first for documents */}
      {isProposal ? (
        <section className="relative isolate flex min-h-[88vh] flex-col" style={{ backgroundColor: C.navy, color: C.sand }} data-testid="mock-hero">
          {TRIP.hero.map((src, i) => (
            <img key={i} src={src} alt="" loading={i === 0 ? "eager" : "lazy"}
              className="absolute inset-0 -z-10 h-full w-full object-cover transition-opacity duration-[1500ms]" style={{ opacity: i === heroIdx ? 0.55 : 0 }} />
          ))}
          <div className="absolute inset-0 -z-10" style={{ background: `linear-gradient(180deg, ${C.navy}d9 0%, ${C.navy}80 35%, ${C.navy}f2 100%)` }} />
          <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-5 py-6 sm:px-8">
            <img src={ET_LOGO_SAND} alt="Espíritu Travel" className="h-9 w-auto sm:h-11" />
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={goToPay} data-testid="mock-hero-book-btn"
                className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-lg transition-opacity hover:opacity-90"
                style={{ backgroundColor: C.terra }}>
                <CheckCircle2 className="h-4 w-4" /> <span>Reserve · €{dueNow.toLocaleString()}</span>
              </button>
              <button onClick={() => window.print()} data-testid="mock-download-btn" className="flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm backdrop-blur transition-colors hover:bg-white/10">
                <Download className="h-4 w-4" /> <span className="hidden sm:inline">Download</span>
              </button>
            </div>
          </div>
          <div className="relative mx-auto flex w-full max-w-5xl flex-1 flex-col justify-end px-5 pb-16 sm:px-8 sm:pb-24">
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: C.terra }}>
              <Sparkles className="h-4 w-4" /> {TRIP.destination}
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }} className="max-w-3xl text-4xl font-bold italic leading-[1.04] sm:text-6xl" style={KANIT} data-testid="mock-title">{TRIP.title}</motion.h1>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }} className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm">
              <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" style={{ color: C.terra }} />{TRIP.start} – {TRIP.end}</span>
              <span className="flex items-center gap-2"><Compass className="h-4 w-4" style={{ color: C.terra }} />{TRIP.days} days · {TRIP.nights} nights</span>
              <span className="flex items-center gap-2"><Users className="h-4 w-4" style={{ color: C.terra }} />{TRIP.travellers} travellers</span>
            </motion.div>
            <div className="mt-8 flex w-fit items-center gap-3 rounded-full border border-white/15 bg-white/10 py-1.5 pl-1.5 pr-5 backdrop-blur" data-testid="mock-specialist">
              <img src={TRIP.specialist.photo} alt={TRIP.specialist.name} className="h-11 w-11 rounded-full object-cover" style={{ boxShadow: `0 0 0 2px ${C.terra}` }} />
              <div className="leading-tight">
                <p className="text-[9px] font-semibold uppercase tracking-[0.2em]" style={{ color: C.terra }}>{TRIP.specialist.role}</p>
                <p className="text-sm font-bold" style={{ color: C.sand }}>{TRIP.specialist.name}</p>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="px-5 pt-16 pb-10 sm:px-8" style={{ backgroundColor: C.navy, color: C.sand }} data-testid="mock-hero">
          <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
            <img src={ET_LOGO_SAND} alt="Espíritu Travel" className="mb-8 h-10 w-auto" />
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: C.terra }}>Travel Documents · {TRIP.destination}</p>
            <h1 className="text-3xl font-bold italic leading-tight sm:text-5xl" style={KANIT} data-testid="mock-title">{TRIP.title}</h1>
            <p className="mt-5 text-sm opacity-90">{TRIP.client} · {TRIP.start} – {TRIP.end} · {TRIP.days} days</p>
            <button onClick={() => window.print()} data-testid="mock-download-btn" className="mt-7 flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: C.terra }}>
              <Download className="h-4 w-4" /> Download documents
            </button>
          </div>
        </section>
      )}

      {/* DOCUMENTS — contact + country info at the very start */}
      {!isProposal && (
        <section className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="mock-docs-intro">
          <Reveal>
            <div className="rounded-2xl border p-6" style={{ borderColor: `${C.navy}1a`, background: "#fff" }}>
              <h2 className="flex items-center gap-2 text-2xl" style={KANIT}><Phone className="h-5 w-5" style={{ color: C.terra }} /> How to reach us</h2>
              <p className="mt-2 text-sm" style={{ color: `${C.navy}b3` }}>We're with you every step. Save these before you travel — our in-destination line is answered 24/7.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <Detail Icon={Phone}>{TRIP.contact.phone}</Detail>
                <Detail Icon={MessageCircle}>WhatsApp · {TRIP.contact.whatsapp}</Detail>
                <Detail Icon={Mail}>{TRIP.contact.email}</Detail>
                <Detail Icon={ShieldCheck}>Emergency · {TRIP.contact.emergency}</Detail>
              </div>
            </div>
            <div className="mt-6">
              <h2 className="mb-3 flex items-center gap-2 text-2xl" style={KANIT}><Globe2 className="h-5 w-5" style={{ color: C.olive }} /> Good to know · Spain & Morocco</h2>
              <div className="grid gap-4 sm:grid-cols-3">
                {TRIP.country.map((c, i) => (
                  <div key={i} className="rounded-xl border p-4" style={{ borderColor: `${C.navy}14` }}>
                    <c.Icon className="h-5 w-5" style={{ color: C.terra }} />
                    <p className="mt-2 font-semibold" style={{ color: C.navy }}>{c.t}</p>
                    <p className="mt-1 text-sm" style={{ color: `${C.navy}b3` }}>{c.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* Sticky day navigation */}
      <nav className="sticky top-0 z-40 border-b backdrop-blur-xl print:hidden" style={{ background: `${C.sand}d9`, borderColor: `${C.navy}1a` }} data-testid="mock-day-nav">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 sm:px-8">
          <img src={ET_LOGO_NAVY} alt="Espíritu Travel" className="hidden h-6 w-auto shrink-0 sm:block" />
          <div className="flex flex-1 gap-1 overflow-x-auto py-2.5" style={{ scrollbarWidth: "none" }}>
            {DAYS.map((d) => {
              const on = active === d.id;
              return (
                <button key={d.id} onClick={() => goTo(d.id)} data-testid={`mock-day-tab-${d.id}`}
                  className="relative shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors"
                  style={{ color: on ? C.navy : `${C.navy}80`, background: on ? "#fff" : "transparent" }}>
                  {d.tab}
                  {on && <span className="absolute -bottom-[11px] left-1/2 h-0.5 w-7 -translate-x-1/2 rounded-full" style={{ background: C.terra }} />}
                </button>
              );
            })}
          </div>
          {isProposal && !approved && (
            <button onClick={goToPay} data-testid="mock-nav-book-btn"
              className="ml-1 flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold text-white shadow transition-opacity hover:opacity-90"
              style={{ backgroundColor: C.terra }}>
              <CheckCircle2 className="h-4 w-4" /> <span>Reserve</span>
            </button>
          )}
        </div>
      </nav>

      {/* Intro (proposal only) */}
      {isProposal && (
        <section className="mx-auto max-w-3xl px-5 py-16 text-center sm:px-8 sm:py-20">
          <Reveal>
            <h2 className="mb-4 text-3xl sm:text-4xl" style={KANIT}>Your journey ahead</h2>
            <p className="text-lg leading-relaxed" style={{ color: `${C.navy}cc` }}>{TRIP.intro}</p>
          </Reveal>
        </section>
      )}

      {/* Day-by-day feed */}
      <div className={`mx-auto px-5 pb-10 sm:px-8 ${compact ? "max-w-3xl pt-8" : "max-w-5xl"}`}>
        {DAYS.map((d, di) => (
          <section key={d.id} id={d.id} ref={(el) => (sectionRefs.current[d.id] = el)}
            className="scroll-mt-24 border-t py-12 first:border-t-0 sm:py-16" style={{ borderColor: `${C.navy}14` }} data-testid={`mock-day-${di}`}>
            <Reveal>
              <div className="mb-8 flex items-baseline gap-4">
                <span className="text-5xl font-bold leading-none sm:text-6xl" style={{ ...KANIT, color: `${C.navy}1f` }}>{String(di + 1).padStart(2, "0")}</span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: C.terra }}>{d.date}</p>
                  <h3 className="text-2xl sm:text-3xl" style={KANIT}>{d.city}</h3>
                </div>
              </div>
            </Reveal>
            <div className="space-y-10 sm:pl-2">
              {d.blocks.map((b, bi) => (<Reveal key={bi} delay={0.03}>{renderBlock(b, di, bi, compact)}</Reveal>))}
            </div>
          </section>
        ))}
      </div>

      {/* PROPOSAL — includes / excludes / conditions / price / approve */}
      {isProposal && (
        <>
          <section className="px-5 py-16 sm:px-8" style={{ background: "#fff" }} data-testid="mock-inclusions">
            <Reveal className="mx-auto grid max-w-5xl gap-10 sm:grid-cols-2">
              <IncExcCol title="What's included" items={TRIP.includes} Icon={CheckCircle2} color={C.olive} />
              <IncExcCol title="What's not included" items={TRIP.excludes} Icon={XCircle} color={C.terra} />
            </Reveal>
          </section>

          <section className="mx-auto max-w-5xl px-5 py-14 sm:px-8" data-testid="mock-conditions">
            <Reveal className="grid gap-8 sm:grid-cols-2">
              <div>
                <h3 className="mb-2 flex items-center gap-2 text-xl" style={KANIT}><ShieldCheck className="h-5 w-5" style={{ color: C.olive }} /> Booking conditions</h3>
                <p className="leading-relaxed" style={{ color: `${C.navy}cc` }}>{TRIP.booking}</p>
              </div>
              <div>
                <h3 className="mb-2 flex items-center gap-2 text-xl" style={KANIT}><Info className="h-5 w-5" style={{ color: C.terra }} /> Cancellation policy</h3>
                <p className="leading-relaxed" style={{ color: `${C.navy}cc` }}>{TRIP.cancellation}</p>
              </div>
            </Reveal>
          </section>

          <section ref={approveRef} className="px-5 py-20 sm:px-8" style={{ backgroundColor: C.navy, color: C.sand }} data-testid="mock-approve">
            <Reveal className="mx-auto max-w-2xl text-center">
              {approved ? (
                <div data-testid="mock-approved">
                  <CheckCircle2 className="mx-auto mb-4 h-12 w-12" style={{ color: C.olive }} />
                  <h2 className="text-3xl font-bold italic sm:text-4xl" style={KANIT}>Booked — you're going!</h2>
                  <p className="mx-auto mt-4 max-w-xl leading-relaxed opacity-90">
                    Thank you, {TRIP.client.split(" ")[0]}! We've received your payment of <strong>€{(paid?.amount ?? dueNow).toLocaleString()}</strong>
                    {paid?.pct === 100 || payInFull
                      ? " (full payment — your trip is within 60 days). You're all set!"
                      : " (30% deposit). The remaining balance is automatically scheduled for 45 days before departure."}
                  </p>
                  {paid?.emailed && (
                    <p className="mx-auto mt-3 max-w-xl text-sm opacity-75" data-testid="mock-receipt-emailed">
                      <Mail className="mr-1.5 inline h-4 w-4" style={{ color: C.terra }} />
                      We've emailed your receipt{paid?.recipient ? ` to ${paid.recipient}` : ""}.
                    </p>
                  )}
                  {paid?.receiptUrl && (
                    <a href={paid.receiptUrl} target="_blank" rel="noreferrer" data-testid="mock-receipt-btn"
                      className="mx-auto mt-6 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold text-white transition-opacity hover:opacity-90"
                      style={{ backgroundColor: C.terra }}>
                      <Download className="h-5 w-5" /> Download receipt {paid?.receiptNo ? `· ${paid.receiptNo}` : ""}
                    </a>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: C.terra }}>Reserve your journey</p>
                  <p className="mt-2 text-5xl font-bold italic sm:text-6xl" style={KANIT}>{TRIP.pricePerPerson}</p>
                  <p className="mt-1 text-sm uppercase tracking-[0.2em] opacity-80">per person · based on {TRIP.travellers} sharing</p>

                  {/* Demo: alterna la fecha de salida para ver la regla de los 60 días */}
                  <div className="mx-auto mt-6 flex w-fit items-center gap-1 rounded-full border border-white/20 p-1 text-xs print:hidden" data-testid="mock-demo-toggle">
                    <button onClick={() => setDaysUntil(120)} data-testid="demo-far"
                      className="rounded-full px-3 py-1.5 font-semibold transition-colors"
                      style={{ background: !payInFull ? C.terra : "transparent", color: "#fff" }}>Salida &gt; 60 días</button>
                    <button onClick={() => setDaysUntil(30)} data-testid="demo-near"
                      className="rounded-full px-3 py-1.5 font-semibold transition-colors"
                      style={{ background: payInFull ? C.terra : "transparent", color: "#fff" }}>Salida &lt; 60 días</button>
                  </div>

                  <div className="mx-auto mt-5 max-w-md rounded-xl border border-white/15 bg-white/5 p-5" data-testid="mock-duenow">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-70">{payInFull ? "Due now · full payment" : "Due now · 30% deposit"}</p>
                    <p className="mt-1 text-3xl font-bold" style={KANIT}>€{dueNow.toLocaleString()}</p>
                    <p className="mt-1 text-xs opacity-75">
                      {payInFull
                        ? "Your trip starts in under 60 days, so the full amount is due to confirm."
                        : `Reserve your dates today; the remaining €${(TOTAL - dueNow).toLocaleString()} is scheduled automatically before departure.`}
                    </p>
                    <div className="mt-4">
                      {!showPay ? (
                        <button onClick={() => setShowPay(true)} data-testid="mock-book-btn"
                          className="inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white transition-opacity hover:opacity-90"
                          style={{ backgroundColor: C.terra }}>
                          <CheckCircle2 className="h-5 w-5" /> Book this trip · pay €{dueNow.toLocaleString()} <ArrowRight className="h-4 w-4" />
                        </button>
                      ) : ppClientId ? (
                        <PayPalScriptProvider options={{ clientId: ppClientId, currency: "EUR", intent: "capture" }}>
                          <PayPalButtons
                            forceReRender={[daysUntil, dueNow]}
                            disabled={paying}
                            style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay", height: 45 }}
                            createOrder={async () => {
                              const r = await fetch(`${API}/api/paypal/create-order`, {
                                method: "POST", headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  total_amount: TOTAL, days_until: daysUntil, trip_ref: "ann-marie-demo",
                                  traveler_name: TRIP.client, trip_title: TRIP.title,
                                }),
                              });
                              const d = await r.json();
                              return d.id;
                            }}
                            onApprove={async (data) => {
                              setPaying(true); setPayError("");
                              try {
                                const r = await fetch(`${API}/api/paypal/capture-order/${data.orderID}`, { method: "POST" });
                                const d = await r.json();
                                if (d.status === "COMPLETED") {
                                  setPaid({
                                    amount: Number(d.capture?.amount ?? dueNow),
                                    pct: payInFull ? 100 : 30,
                                    receiptUrl: d.receipt?.url ? `${API}${d.receipt.url}` : null,
                                    receiptNo: d.receipt?.receipt_no || null,
                                    emailed: !!d.receipt?.emailed,
                                    recipient: d.receipt?.recipient || null,
                                  });
                                  setApproved(true);
                                  setTimeout(() => approveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
                                } else {
                                  setPayError("We couldn't confirm your payment yet. Please try again or contact us.");
                                }
                              } catch (e) {
                                setPayError("Something went wrong finalising your payment. Please try again.");
                              } finally {
                                setPaying(false);
                              }
                            }}
                            onError={() => setPayError("Payment could not be completed. Please try again.")}
                            onCancel={() => { setPaying(false); setPayError(""); }}
                          />
                        </PayPalScriptProvider>
                      ) : (
                        <p className="text-xs opacity-70">Cargando pago seguro…</p>
                      )}
                      {paying && (
                        <p className="mt-3 flex items-center justify-center gap-2 text-sm opacity-85" data-testid="mock-paying">
                          <Clock className="h-4 w-4 animate-spin" style={{ color: C.terra }} /> Processing your payment…
                        </p>
                      )}
                      {payError && (
                        <p className="mt-3 text-sm" style={{ color: "#fecaca" }} data-testid="mock-pay-error">{payError}</p>
                      )}
                    </div>
                  </div>
                  <p className="mt-4 text-[11px] opacity-60">Pago de prueba · PayPal Sandbox (sin dinero real)</p>
                </>
              )}
            </Reveal>
          </section>
        </>
      )}

      {/* Footer */}
      <footer className="px-5 py-16 text-center sm:px-8" style={{ backgroundColor: C.navy, color: C.sand }}>
        <h2 className="text-3xl font-bold italic sm:text-4xl" style={KANIT}>Feel part of the world</h2>
        {!isProposal && (
          <p className="mx-auto mt-4 max-w-md text-sm opacity-80">Questions on the road? Call {TRIP.contact.phone} or WhatsApp {TRIP.contact.whatsapp} — we're here 24/7.</p>
        )}
        <div className="mx-auto mt-10 flex w-fit flex-col items-center gap-4 border-t border-white/15 pt-8">
          <span className="text-[11px] uppercase tracking-[0.3em] opacity-60">Crafted by</span>
          <img src={ET_LOGO_SAND} alt="Espíritu Travel" className="h-12 w-auto sm:h-14" />
        </div>
      </footer>
    </div>
  );
}
